import { escapeRegExp } from "lodash";
import { createElement, type ReactNode } from "react";

const DEFAULT_ALLOWED_TAGS = ["strong", "b", "em", "i", "u"];

/**
 * Renders a whitelist of simple inline tags (default: strong, b, em, i, u)
 * in a localized string as React elements. Localization strings are plain
 * text, so React escapes markup in them by default; this lets a translated
 * string call out a word or phrase with emphasis. Any tag not in the
 * whitelist is left as literal text rather than being turned into an
 * element, so only explicitly allowed tags can ever affect the DOM.
 *
 * Assumes well-formed markup: tags must be properly nested or sibling
 * (e.g. `<strong>a</strong><em>b</em>` or `<strong>a <em>b</em></strong>`).
 * Overlapping tags (e.g. `<b><strong>x</b></strong>`) are not detected as
 * malformed and will mis-parse rather than being rejected or fixed up.
 */
export function formatLocalizedMarkup(
  text: string,
  allowedTags: string[] = DEFAULT_ALLOWED_TAGS,
): ReactNode[] {
  if (allowedTags.length === 0) {
    return [text];
  }

  const escapedTags = allowedTags.map(escapeRegExp);
  const tagPattern = new RegExp(
    String.raw`<(${escapedTags.join("|")})>(.*?)<\/\1>`,
    "gs",
  );

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (
    let match = tagPattern.exec(text);
    match !== null;
    match = tagPattern.exec(text)
  ) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, tagName, innerText] = match;
    parts.push(
      createElement(
        tagName,
        { key: key++ },
        ...formatLocalizedMarkup(innerText, allowedTags),
      ),
    );
    lastIndex = tagPattern.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
